"use client";

// EvolutionScreen — animated evolution sequence.
//
// Pure / presentational. It is handed a single, already-decided EvolutionEvent
// (from `@/engine/evolution`) and plays the classic 4-stage Pokémon-style
// sequence: flash → silhouette → reveal → celebrate. After the full timeline it
// calls `onComplete`. While the bright stages play (flash / silhouette) the
// player may press X / Escape to cancel — but only if `onCancel` is provided.
// After the reveal the evolution is visually committed and cancel is ignored.
//
// All data arrives via props and all outcomes leave via callbacks. This
// component imports NO game store and never mutates engine state — the shell is
// responsible for mounting it and for applying the evolution to the party once
// `onComplete` fires.
//
// Palette is strict cream + ink + accent ramp (PIXEL_STYLE.md). The single
// allowed deviation is the full-screen white flash during the 'flash' and
// 'reveal' stages — that brightness IS the dramatic beat.

import { useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, JSX } from "react";

import { BattleSprite } from "@/components/game/BattleSprite";
import { BattleEffects, type EffectRequest } from "@/components/game/BattleEffects";
import { PixelText } from "@/components/ui/pixel-text";
import { type SpeciesData } from "@/data/species";
import { playSfx } from "@/engine/audio";
import { resolveSpecies } from "@/engine/battle-participant";
import {
  EVOLUTION_ANIM_MS,
  EVOLUTION_STAGES,
  type EvolutionEvent,
} from "@/engine/evolution";

// ── Public API ──────────────────────────────────────────────────────────────

export type EvolutionScreenProps = {
  /** The already-decided evolution to animate. */
  event: EvolutionEvent;
  /** Fired once after the full timeline finishes (evolution committed). */
  onComplete: () => void;
  /**
   * Optional. When provided, the player may press X / Escape during the
   * flash / silhouette stages to abort. Omit it to make the evolution
   * unstoppable (e.g. scripted story evolutions).
   */
  onCancel?: () => void;
};

// ── Stage timing (read from the engine so UI + engine stay in lockstep) ─────

type StageLabel = "flash" | "silhouette" | "reveal" | "celebrate";

type StageWindow = { label: StageLabel; startMs: number; endMs: number };

const STAGE_WINDOWS: StageWindow[] = (() => {
  const labels: StageLabel[] = ["flash", "silhouette", "reveal", "celebrate"];
  const windows: StageWindow[] = [];
  for (let i = 0; i < EVOLUTION_STAGES.length; i++) {
    const start = EVOLUTION_STAGES[i].atMs;
    const end =
      i + 1 < EVOLUTION_STAGES.length ? EVOLUTION_STAGES[i + 1].atMs : EVOLUTION_ANIM_MS;
    const rawLabel = EVOLUTION_STAGES[i].label;
    const label = (labels.includes(rawLabel as StageLabel)
      ? (rawLabel as StageLabel)
      : labels[i]) as StageLabel;
    windows.push({ label, startMs: start, endMs: end });
  }
  return windows;
})();

function stageAt(elapsedMs: number): StageLabel {
  for (const win of STAGE_WINDOWS) {
    if (elapsedMs >= win.startMs && elapsedMs < win.endMs) return win.label;
  }
  return "celebrate";
}

// ── Species lookup helpers ──────────────────────────────────────────────────

function lookupSpecies(speciesId: string): SpeciesData | undefined {
  return resolveSpecies(speciesId);
}

function displayName(speciesId: string): string {
  return lookupSpecies(speciesId)?.name ?? `#${speciesId}`;
}

// ── Palette tokens (mirrors PIXEL_STYLE.md) ─────────────────────────────────

const COLOR_CREAM = "#f5ecd6";
const COLOR_INK = "#1a1a1a";
const COLOR_WALNUT = "#5c4a30";

// ── Centered burst wrapper ──────────────────────────────────────────────────
// BattleEffects expects pixel coordinates; we wrap it in a relatively-sized box
// and rewrite the origin to the box's center so the burst always blooms from
// the new sprite.

function CenteredBurst({ effects }: { effects: EffectRequest[] }): JSX.Element {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [resolved, setResolved] = useState<EffectRequest[]>([]);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const cx = rect.width / 2;
    const cy = rect.height / 2;
    setResolved(
      effects.map((e) => ({
        ...e,
        origin: { x: cx, y: cy },
      })),
    );
  }, [effects]);

  return (
    <div ref={wrapRef} className="absolute inset-0 pointer-events-none">
      <BattleEffects effects={resolved} />
    </div>
  );
}

// ── Top-level component ─────────────────────────────────────────────────────

export function EvolutionScreen(props: EvolutionScreenProps): JSX.Element {
  const { event, onComplete, onCancel } = props;
  const fromSpeciesId = event.fromSpeciesId;
  const toSpeciesId = event.toSpeciesId;
  // Prefer the player's nickname for the "Your X evolved" copy; fall back to
  // the species dex name.
  const fromLabel = event.participantSnapshot.nickname?.trim() || displayName(fromSpeciesId);
  const cancelable = onCancel !== undefined;

  const [elapsed, setElapsed] = useState(0);
  const [done, setDone] = useState(false);
  const startedAtRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);
  const completedRef = useRef(false);
  const flashSoundFiredRef = useRef(false);
  const celebrateSoundFiredRef = useRef(false);
  const cancelRequestedRef = useRef(false);

  // Single 'evolution-burst' particle effect — fires on entering 'celebrate'.
  const [burstEffects, setBurstEffects] = useState<EffectRequest[]>([]);
  const burstFiredRef = useRef(false);

  const stage = stageAt(elapsed);

  // Drive the animation clock.
  useEffect(() => {
    function tick(now: number) {
      if (startedAtRef.current === null) startedAtRef.current = now;
      const e = now - startedAtRef.current;
      if (e >= EVOLUTION_ANIM_MS) {
        setElapsed(EVOLUTION_ANIM_MS);
        setDone(true);
        return;
      }
      setElapsed(e);
      rafRef.current = requestAnimationFrame(tick);
    }
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  // Fire 'evolution' SFX on entering the very first stage.
  useEffect(() => {
    if (stage === "flash" && !flashSoundFiredRef.current) {
      flashSoundFiredRef.current = true;
      playSfx("evolution");
    }
  }, [stage]);

  // Fire 'level_up' + particle burst on entering 'celebrate'.
  useEffect(() => {
    if (stage !== "celebrate") return;
    if (!celebrateSoundFiredRef.current) {
      celebrateSoundFiredRef.current = true;
      playSfx("level_up");
    }
    if (!burstFiredRef.current) {
      burstFiredRef.current = true;
      setBurstEffects([
        {
          id: `evo-burst-${Date.now()}`,
          kind: "evolution-burst",
          origin: { x: 0.5, y: 0.5 }, // overlay handles centering via its own coords
          type: lookupSpecies(toSpeciesId)?.type1,
        },
      ]);
    }
  }, [stage, toSpeciesId]);

  // Cancel handling: X / Escape must be pressed during flash or silhouette.
  // After silhouette the evolution is locked in, even if X is pressed.
  useEffect(() => {
    if (!cancelable) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== "x" && e.key !== "X" && e.key !== "Escape") return;
      if (stage !== "flash" && stage !== "silhouette") return;
      if (cancelRequestedRef.current) return;
      cancelRequestedRef.current = true;
      playSfx("menu_close");
      onCancel?.();
      e.preventDefault();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [cancelable, stage, onCancel]);

  // Settle: fire onComplete once after the full timeline (unless cancelled).
  useEffect(() => {
    if (!done) return;
    if (completedRef.current) return;
    if (cancelRequestedRef.current) return;
    completedRef.current = true;
    onComplete();
  }, [done, onComplete]);

  // ── Visual layers ────────────────────────────────────────────────────────

  // Background darkens during silhouette, flashes near-white during flash + reveal.
  const flashOpacity = useMemo(() => {
    if (stage === "flash") {
      // 0 → 1 → 0 across the flash window.
      const win = STAGE_WINDOWS.find((s) => s.label === "flash");
      if (!win) return 0;
      const t = (elapsed - win.startMs) / Math.max(1, win.endMs - win.startMs);
      // Peak at t=0.5
      return Math.max(0, 1 - Math.abs(t - 0.5) * 2);
    }
    if (stage === "reveal") {
      const win = STAGE_WINDOWS.find((s) => s.label === "reveal");
      if (!win) return 0;
      const t = (elapsed - win.startMs) / Math.max(1, win.endMs - win.startMs);
      // Sharp flash at the start, gone by the end.
      return Math.max(0, 1 - t * 1.4);
    }
    return 0;
  }, [stage, elapsed]);

  const backdropDark = stage === "silhouette" || stage === "reveal" || stage === "celebrate";

  // Show the FROM sprite during flash + silhouette (silhouette filters it),
  // swap to the TO sprite once we hit reveal.
  const showFromSprite = stage === "flash" || stage === "silhouette";
  const isSilhouette = stage === "silhouette";

  // Reveal pulse: tiny scale-up on the new sprite for the first ~200ms of reveal.
  const revealPulse = useMemo(() => {
    if (stage !== "reveal") return 1;
    const win = STAGE_WINDOWS.find((s) => s.label === "reveal");
    if (!win) return 1;
    const t = (elapsed - win.startMs) / Math.max(1, win.endMs - win.startMs);
    if (t > 0.4) return 1;
    return 1 + (1 - t / 0.4) * 0.15;
  }, [stage, elapsed]);

  const showCompletionText = stage === "celebrate";

  const containerStyle: CSSProperties = {
    fontFamily: "monospace",
    imageRendering: "pixelated",
  };

  return (
    <div className="fixed inset-0 z-50" style={containerStyle} data-testid="evolution-screen">
      <div
        role="dialog"
        aria-label="Evolution sequence"
        className="absolute inset-0 overflow-hidden"
        style={{
          background: backdropDark ? COLOR_INK : COLOR_CREAM,
          transition: "background 200ms steps(3)",
          imageRendering: "pixelated",
        }}
        data-testid="evolution-animation"
        data-stage={stage}
      >
        {/* Sprite stage */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div
            style={{
              transform: `scale(${revealPulse})`,
              transformOrigin: "center center",
              transition: "transform 60ms steps(3)",
              filter: isSilhouette
                ? // Pure-ink silhouette: brightness to 0 stamps the shape in ink.
                  "brightness(0) saturate(100%) invert(8%) sepia(0%) saturate(0%) hue-rotate(0deg)"
                : undefined,
            }}
          >
            {showFromSprite ? (
              <BattleSprite speciesId={fromSpeciesId} orientation="front" scale={4} state="idle" battlePortrait />
            ) : (
              <BattleSprite speciesId={toSpeciesId} orientation="front" scale={4} state="idle" battlePortrait />
            )}
          </div>
        </div>

        {/* Particle burst layer (celebrate only) */}
        {burstEffects.length > 0 && <CenteredBurst effects={burstEffects} />}

        {/* Pure-color flash overlay — the one sanctioned bright-white moment */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            background: "#ffffff",
            opacity: flashOpacity,
          }}
        />

        {/* "X evolved into Y" message */}
        {showCompletionText && (
          <div
            className="absolute inset-x-0 bottom-12 flex justify-center"
            style={{ pointerEvents: "none" }}
          >
            <div
              className="px-6 py-3"
              style={{
                background: COLOR_CREAM,
                border: `2px solid ${COLOR_INK}`,
                boxShadow: `inset 1px 1px 0 ${COLOR_CREAM}, inset -1px -1px 0 ${COLOR_WALNUT}`,
                color: COLOR_INK,
                imageRendering: "pixelated",
              }}
            >
              <PixelText as="div" glow="none" size="md" style={{ color: COLOR_INK }}>
                Your {fromLabel} evolved into {displayName(toSpeciesId)}!
              </PixelText>
            </div>
          </div>
        )}

        {/* Hint: cancel availability */}
        {cancelable && (stage === "flash" || stage === "silhouette") && (
          <div
            className="absolute top-4 right-4 text-xs uppercase tracking-widest"
            style={{ color: COLOR_CREAM }}
          >
            Hold X to cancel
          </div>
        )}
      </div>
    </div>
  );
}

export default EvolutionScreen;
